import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROTAS } from '@remoto/shared';
import { chamar } from '../api';
import { BotaoLeve, Cartao } from './layout';
import { mensagemDoErro } from '../erros';

interface Resumo {
  id: string;
  tipo: 'RESUMO' | 'ATA' | 'RESPOSTA';
  pergunta: string | null;
  conteudoMd: string;
  criadoEm: string;
}

const NOME: Record<Resumo['tipo'], string> = {
  RESUMO: 'Resumo',
  ATA: 'Ata',
  RESPOSTA: 'Resposta',
};

/**
 * Resumo, ata e pergunta de uma gravação.
 *
 * Cada pedido demora — o modelo lê a transcrição inteira — então o botão fica
 * ocupado até a resposta chegar, e o texto pronto entra na lista sem recarregar
 * a página.
 */
export function Resumos({ gravacaoId }: { gravacaoId: string }): React.JSX.Element {
  const fila = useQueryClient();
  const [pergunta, setPergunta] = useState('');
  const [erro, setErro] = useState('');

  const { data: resumos } = useQuery({
    queryKey: ['resumos', gravacaoId],
    queryFn: () => chamar<Resumo[]>(`${ROTAS.gravacoes}/${gravacaoId}/resumos`),
  });

  const gerar = useMutation({
    mutationFn: (pedido: { tipo: 'RESUMO' | 'ATA' } | { pergunta: string }) =>
      'pergunta' in pedido
        ? chamar<Resumo>(`${ROTAS.gravacoes}/${gravacaoId}/perguntas`, {
            method: 'POST',
            corpo: pedido,
          })
        : chamar<Resumo>(`${ROTAS.gravacoes}/${gravacaoId}/resumos`, {
            method: 'POST',
            corpo: pedido,
          }),
    onSuccess: () => {
      setErro('');
      setPergunta('');
      void fila.invalidateQueries({ queryKey: ['resumos', gravacaoId] });
    },
    onError: (e) =>
      setErro(
        mensagemDoErro(e, 'Não foi possível gerar o texto.', {
          vps_offline: 'Nenhuma máquina no ar para pensar o texto.',
        }),
      ),
  });

  return (
    <Cartao
      titulo="Resumo e ata"
      acao={
        <div className="flex gap-2">
          <BotaoLeve disabled={gerar.isPending} onClick={() => gerar.mutate({ tipo: 'RESUMO' })}>
            Gerar resumo
          </BotaoLeve>
          <BotaoLeve disabled={gerar.isPending} onClick={() => gerar.mutate({ tipo: 'ATA' })}>
            Gerar ata
          </BotaoLeve>
        </div>
      }
    >
      <form
        className="flex gap-2"
        onSubmit={(evento) => {
          evento.preventDefault();
          if (pergunta.trim().length >= 3) gerar.mutate({ pergunta: pergunta.trim() });
        }}
      >
        <input
          value={pergunta}
          onChange={(e) => setPergunta(e.target.value)}
          placeholder="Perguntar sobre este áudio..."
          className="flex-1 rounded-md border border-zinc-700 bg-zinc-950 px-3 py-1.5 text-sm text-zinc-200 placeholder:text-zinc-600"
        />
        <BotaoLeve type="submit" disabled={gerar.isPending || pergunta.trim().length < 3}>
          Perguntar
        </BotaoLeve>
      </form>

      {gerar.isPending ? (
        <p className="mt-3 text-sm text-zinc-500">
          Pensando... isso leva alguns minutos numa gravação longa.
        </p>
      ) : null}
      {erro ? <p className="mt-3 text-sm text-rose-400">{erro}</p> : null}

      {resumos && resumos.length > 0 ? (
        <div className="mt-4 space-y-4">
          {resumos.map((r) => (
            <article key={r.id} className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-4">
              <p className="mb-2 text-xs text-zinc-500">
                {NOME[r.tipo]}
                {r.pergunta ? ` · ${r.pergunta}` : ''} ·{' '}
                {new Date(r.criadoEm).toLocaleString('pt-BR')}
              </p>
              <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-zinc-300">
                {r.conteudoMd}
              </pre>
            </article>
          ))}
        </div>
      ) : (
        <p className="mt-3 text-sm text-zinc-500">Nenhum texto gerado ainda.</p>
      )}
    </Cartao>
  );
}
